/**
 * DM API の error_type 判定ユーティリティ（REECH-3514）
 */

export const ERROR_TYPE_PROXY = "proxy_error";
export const ERROR_TYPE_NETWORK = "network_error";
export const ERROR_TYPE_LOGIN_REQUIRED = "login_required";
export const ERROR_TYPE_CHALLENGE_REQUIRED = "challenge_required";

const DEFAULT_NETWORK_MESSAGE =
  "通信環境の一時的な障害により Instagram 連携サーバーに接続できません。しばらく時間をおいて再度お試しください。";

const DEFAULT_LOGIN_REQUIRED_MESSAGE =
  "Instagramとの連携が解除されています。再度連携を行ってください。";

const DEFAULT_CHALLENGE_REQUIRED_MESSAGE =
  "Instagram側で追加認証が必要です。連携設定から再連携してください。";

const RAW_AUTH_MESSAGES = [
  "login_required",
  "challenge_required",
  "user_has_logged_out",
];

/**
 * JSON 文字列を安全に parse する
 * @param {unknown} value
 * @returns {Object|null}
 */
function tryParseJsonObject(value) {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * ExternalApi 包み込みレスポンスから Instagram message を取り出す
 * （HTTP 200 でも body が 403 + login_required のケース）
 * @param {Object} data
 * @returns {string|null}
 */
export function extractNestedInstagramMessage(data) {
  if (!data || typeof data !== "object") return null;
  if (typeof data.message === "string" && data.message.trim() !== "") {
    return data.message;
  }
  const fromException = tryParseJsonObject(data.exception && data.exception.message);
  if (fromException && typeof fromException.message === "string") {
    return fromException.message;
  }
  const fromBody = tryParseJsonObject(data.response && data.response.body);
  if (fromBody && typeof fromBody.message === "string") {
    return fromBody.message;
  }
  return null;
}

/**
 * 連携解除・追加認証系か
 * @param {Object} data API レスポンス
 * @returns {boolean}
 */
export function isLoginRequiredError(data) {
  if (!data) return false;
  if (
    data.error_type === ERROR_TYPE_LOGIN_REQUIRED ||
    data.error_type === ERROR_TYPE_CHALLENGE_REQUIRED
  ) {
    return true;
  }
  if (RAW_AUTH_MESSAGES.includes(data.message)) {
    return true;
  }
  const nested = extractNestedInstagramMessage(data);
  return RAW_AUTH_MESSAGES.includes(nested);
}

/**
 * プロキシ／ネットワーク障害か
 * @param {Object} data API レスポンス
 * @returns {boolean}
 */
export function isNetworkError(data) {
  if (!data) return false;
  return (
    data.error_type === ERROR_TYPE_PROXY ||
    data.error_type === ERROR_TYPE_NETWORK
  );
}

/**
 * ExternalApi のエラー包み込み（成功 inbox ではない）か
 * @param {Object} data
 * @returns {boolean}
 */
export function isExternalApiErrorEnvelope(data) {
  if (!data || typeof data !== "object") return false;
  if (data.exception) return true;
  const statusCode = data.response && data.response.statusCode;
  return typeof statusCode === "number" && statusCode >= 400;
}

/**
 * 連携解除・追加認証向けのユーザー文言
 * @param {Object} data API レスポンス
 * @returns {string}
 */
export function getLoginRequiredErrorText(data) {
  const nested = extractNestedInstagramMessage(data);
  if (
    (data && data.error_type === ERROR_TYPE_CHALLENGE_REQUIRED) ||
    nested === "challenge_required" ||
    (data && data.message === "challenge_required")
  ) {
    if (
      data &&
      data.message &&
      typeof data.message === "string" &&
      !RAW_AUTH_MESSAGES.includes(data.message)
    ) {
      return data.message;
    }
    return DEFAULT_CHALLENGE_REQUIRED_MESSAGE;
  }
  if (
    data &&
    data.message &&
    typeof data.message === "string" &&
    data.message.trim() !== "" &&
    !RAW_AUTH_MESSAGES.includes(data.message) &&
    data.error_type
  ) {
    return data.message;
  }
  return DEFAULT_LOGIN_REQUIRED_MESSAGE;
}

/**
 * ユーザー向けメッセージを取得
 * @param {Object} data API レスポンス
 * @param {string} [fallback]
 * @returns {string}
 */
export function getMessageErrorText(data, fallback = DEFAULT_NETWORK_MESSAGE) {
  if (isLoginRequiredError(data)) {
    return getLoginRequiredErrorText(data);
  }
  if (data && typeof data.message === "string" && data.message.trim() !== "") {
    return data.message;
  }
  return fallback;
}
